'use client'

import { CUT_OUT_MINIMUM_PATH_LENGTH } from 'creation/src/creation-tools/constants'
import { SyntheticEvent, useEffect, useRef } from 'react'
import { overlayBlock } from '../../../presentation/blocks'
import Canvas from '../../../presentation/canvas'
import { useCreationStore } from '../../../store/provider'
import { useCutOut } from '../cut-out-context'
import { pointFromEvent } from '../utils'
import { cn } from '@/util/cn'

const START_POINT_RADIUS = 6
const STROKE_SIZE = 4

type LassoPoint = {
    x: number
    y: number
}

type Props = {
    scale: number
}

const getDistance = (point1: LassoPoint, point2: LassoPoint) =>
    Math.sqrt(Math.pow(point1.x - point2.x, 2) + Math.pow(point1.y - point2.y, 2))

const Lasso = ({ scale }: Props) => {
    const sources = useCreationStore((state) => state.sources)
    const { undoStepCount, setUndoStepCount, editCanvas, checkIfIsTransparentEnoughToContinue, setIsResetEnable } =
        useCutOut()
    const pathRef = useRef<LassoPoint[]>([])
    const pathClosedRef = useRef<boolean>(false)

    const draw = (canvas: HTMLCanvasElement, context: CanvasRenderingContext2D, drawCount: number) => {
        if (!sources.length) return

        const { width, height } = sources[0]
        const path = pathRef.current

        // clear canvas
        canvas.width = width
        canvas.height = height

        // draw path
        if (context && path.length > 0) {
            const isClosed = pathClosedRef.current

            // configure fill and stroke styles
            context.strokeStyle = '#fff'
            context.lineWidth = Math.round(STROKE_SIZE * scale)
            context.lineJoin = 'round'
            context.setLineDash([Math.round(STROKE_SIZE * 2 * scale), Math.round(STROKE_SIZE * scale)])
            context.lineDashOffset = -drawCount * scale

            // draw smoothed path
            if (path.length > 1) {
                context.beginPath()
                path.forEach(({ x, y }, i) => {
                    if (i === 0) {
                        context.moveTo(x, y)
                    } else if (i === path.length - 1) {
                        if (isClosed) {
                            const { x: startX, y: startY } = path[0]
                            const curveX = (x + startX) / 2
                            const curveY = (y + startY) / 2
                            context.quadraticCurveTo(x, y, curveX, curveY)
                            context.quadraticCurveTo(curveX, curveY, startX, startY)
                        } else {
                            context.lineTo(x, y)
                        }
                    } else {
                        // draw smooth path
                        const { x: nextX, y: nextY } = path[i + 1]
                        context.quadraticCurveTo(x, y, (x + nextX) / 2, (y + nextY) / 2)
                    }
                })

                // show path outline if still editing
                !isClosed && context.stroke()
            }

            if (isClosed) {
                const mask = document.createElement('canvas')
                const maskContext = mask.getContext('2d')

                mask.width = canvas.width
                mask.height = canvas.height

                // fill background black
                if (maskContext) {
                    maskContext.fillStyle = '#000'
                    maskContext.fillRect(0, 0, mask.width, mask.height)
                }

                // fill path and draw it to mask canvas
                context.fillStyle = '#fff'
                context.fill()

                maskContext?.drawImage(canvas, 0, 0)

                // clear canvas to prevent mask fill from flashing
                canvas.width = width
                canvas.height = height

                // reset points
                pathRef.current = []
                pathClosedRef.current = false

                // Run the lasso method on edit canvas
                editCanvas.map(({ canvas, frameCount = 1 }) => {
                    const frameImageData = canvas.getContext('2d')?.getImageData(0, 0, canvas.width, canvas.height)
                    // const canvas = document.createElement('canvas')
                    const context = canvas.getContext('2d')
                    // canvas.width = frame.width
                    // canvas.height = frame.height
                    if (mask && context && frameImageData) {
                        const maskImageData = mask.getContext('2d')?.getImageData(0, 0, mask.width, mask.height)
                        const data = maskImageData?.data || []
                        for (let i = 0; i < data.length; i += 4) {
                            const isTranparent = data[i + 3] === 0
                            const isBlack = data[i] === 0 && data[i + 1] === 0 && data[i + 2] === 0
                            if (isTranparent || isBlack) {
                                frameImageData.data[i + 3] = 0
                            }
                        }
                        context.putImageData(frameImageData, 0, 0)
                    }
                    return { canvas, frameCount }
                })

                setUndoStepCount(0)
                setIsResetEnable(true)
                checkIfIsTransparentEnoughToContinue()
            } else {
                // draw path starting point
                context.fillStyle = '#00ff99'
                context.beginPath()
                context.arc(path[0].x, path[0].y, Math.round(START_POINT_RADIUS * scale), 0, Math.PI * 2)
                context.fill()
            }
        }
    }

    const onKeyDown = (e: KeyboardEvent) => {
        // clear selection when esc key is pressed
        if (e.key === 'Escape' && !pathClosedRef.current) {
            pathRef.current = []
            setUndoStepCount(0)
        }
    }

    const onClick = (e: SyntheticEvent<HTMLCanvasElement, Event>) => {
        e.preventDefault()
        const { x, y } = pointFromEvent(e.nativeEvent as MouseEvent, e.currentTarget, scale)
        const path = pathRef.current
        const startPointRadius = START_POINT_RADIUS * scale

        // check if point is close enough to the starting point to close the path
        if (
            path.length >= CUT_OUT_MINIMUM_PATH_LENGTH &&
            Math.abs(getDistance({ x, y }, path[0])) <= startPointRadius
        ) {
            pathClosedRef.current = true
        } else {
            path.push({ x, y })
            setUndoStepCount(undoStepCount + 1)
        }
    }

    // remove points when undo is clicked
    useEffect(() => {
        const path = pathRef.current || []
        if (undoStepCount < path.length) {
            pathRef.current = path.slice(0, undoStepCount)
        }
    }, [undoStepCount])

    // add esc key event
    useEffect(() => {
        window.addEventListener('keydown', onKeyDown, false)
        return () => {
            window.removeEventListener('keydown', onKeyDown, false)
        }
    }, [])

    return (
        <div className={cn(overlayBlock, 'cursor-pointer select-none')}>
            <Canvas className="block h-full w-full" draw={draw} onClick={onClick} />
        </div>
    )
}

export default Lasso
